/**
 * Session-Specific Error Boundary
 * Handles session refresh loops and authentication failures
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './button';
import { Alert, AlertDescription, AlertTitle } from './alert';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isSessionLoop: boolean;
  retryCount: number;
}

export class SessionErrorBoundary extends Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isSessionLoop: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is a session-related error
    const isSessionLoop = error.message.includes('session') ||
                         error.message.includes('refresh') ||
                         error.message.includes('auth');

    return {
      hasError: true,
      error,
      isSessionLoop
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 Session Error Boundary caught error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // If it's a session loop, try recovery
    if (this.state.isSessionLoop) {
      this.attemptSessionRecovery();
    }
  }

  private attemptSessionRecovery = async () => {
    if (this.state.retryCount >= 3) {
      console.error('🚨 Max session recovery attempts reached');
      return;
    }

    console.log('🔄 Attempting session recovery...');
    
    try {
      // Clear all session storage
      sessionStorage.clear();
      
      // Clear auth cache
      localStorage.removeItem('session_validation_cache');
      localStorage.removeItem('evee_session_state');
      
      // Reset session state
      await supabase.auth.refreshSession();
      
      // Auto-retry after cleanup
      this.retryTimeout = setTimeout(() => {
        this.setState(prevState => ({
          hasError: false,
          error: null,
          errorInfo: null,
          retryCount: prevState.retryCount + 1
        }));
      }, 2000);
      
    } catch (error) {
      console.error('❌ Session recovery failed:', error);
    }
  };

  private handleManualRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    });
  };

  private handleForceSignOut = async () => {
    try {
      // Store logout timestamp
      localStorage.setItem('last_logout_time', Date.now().toString());
      
      // Clear all auth storage first for cross-domain compatibility
      const { clearAuthStorage } = await import('@/utils/constants');
      clearAuthStorage();
      
      // Force sign out with global scope
      await supabase.auth.signOut({ scope: 'global' });
      
      // Force redirect to ensure clean state
      window.location.replace('/auth');
    } catch (error) {
      console.error('❌ Force sign out failed:', error);
      
      // Fallback: clear storage and redirect anyway
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {}
      
      window.location.replace('/auth');
    }
  };

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full space-y-4">
            <Alert variant="destructive">
              <AlertTitle>
                {this.state.isSessionLoop ? 'Authentication Issue' : 'Application Error'}
              </AlertTitle>
              <AlertDescription>
                {this.state.isSessionLoop 
                  ? 'We detected an authentication loop. This usually resolves automatically, but you can try the recovery options below.'
                  : 'An unexpected error occurred. Please try refreshing the page.'
                }
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Button 
                onClick={this.handleManualRetry}
                className="w-full"
              >
                Try Again
              </Button>
              
              {this.state.isSessionLoop && (
                <Button 
                  onClick={this.handleForceSignOut}
                  variant="outline"
                  className="w-full"
                >
                  Sign Out & Restart
                </Button>
              )}
              
              <Button 
                onClick={() => {
                  // Use navigation helper for safer reload
                  import('@/hooks/useNavigationHelper').then(async ({ useNavigationHelper }) => {
                    const { safeReload } = useNavigationHelper();
                    await safeReload('session-error-manual-reload');
                  }).catch(() => {
                    console.warn('⚠️ SessionErrorBoundary fallback - attempting navigation recovery');
                    try {
                      window.history.replaceState(null, '', window.location.pathname);
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    } catch (navError) {
                      console.warn('Navigation recovery failed; please refresh manually', navError);
                    }
                  });
                }}
                variant="outline"
                className="w-full"
              >
                Reload Page
              </Button>
            </div>

            {this.state.retryCount > 0 && (
              <p className="text-sm text-muted-foreground text-center">
                Recovery attempt {this.state.retryCount}/3
              </p>
            )}

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-xs">
                <summary className="cursor-pointer">Error Details</summary>
                <pre className="mt-2 p-2 bg-muted rounded overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}